import { AddEventListernerResult, useEventListenersRef } from "@kalyzee/kast-web-sdk/web-components";
import React, { useContext, useMemo, useRef, useState } from "react";
import { Location, NavigateOptions, Params, useLocation, useNavigate } from "react-router-dom";
import { useLogger } from "../../helpers/logger";
// Deep import (not the barrel): the barrel re-exports redirect components that
// import this file — going through it would create a module cycle.
import { applyStickyParams, getStickySearchParams, NavigateGlobalQuery } from "../../helpers/navigation/queries";
import SettingsPopup, { SettingsPopupRef } from "../utils/SettingsPopup";
import { SessionMode } from "../../contexts/appStore/hooks";
import { useAppStoreSelector } from "../../contexts/appStore/selectors";
import { useAppStore } from "../../contexts/appStore/store";
import { AppLoadingView } from "../utils/AppLoadingView";

export enum PageMasterMode {
  LOGIN = "login",
  LOGOUT = "logout",
  FORBIDDEN = "forbidden",
  EMPTY = "empty",
  LIST = "list",
  JOIN = "join",
  LOBBY = "lobby",
  PLAYER = "player",
  COMPOSITION = "composition",
  MEETING = "meeting",
  MEETING_CALL = "meeting-call",
  MEETING_DASHBOARD = "meeting-dashboard",
  MEETING_HIGHLIGHT = "meeting-highlight",
  MEETING_GROUP = "meeting-group",
  MEETING_PEDAGOGY = "meeting-pedagogy",
  MEETING_COMMUNICATION = "meeting-communication",
  DASHBOARD = 'dashboard',
  OVERVIEW = 'overview',
  WAITING_ROOM = "waiting-room",
}

export enum PageSlaveMode {
  MEETING = "meeting",
}

/**
 * The composition page takes an optional id (`/composition` = the first one).
 * React Router's optional segments would break `getPageUrl`'s `/:id`
 * substitution, so the bare path is registered as its own route — hence this
 * constant, shared by PAGE_PATHS and the route trees.
 */
export const COMPOSITION_ROOT_PATH = "/composition";

export const PageMap = {
  [SessionMode.MASTER]: PageMasterMode,
  [SessionMode.SLAVE]: PageSlaveMode,
} as const;
export interface PageMap {
  [SessionMode.MASTER]: PageMasterMode;
  [SessionMode.SLAVE]: PageSlaveMode;
}

export const PAGE_PATHS: { [k in SessionMode]: { [key in PageMap[k]]: string } } = {
  [SessionMode.MASTER]: {
    [PageMasterMode.LOGIN]: "/login",
    [PageMasterMode.LOGOUT]: "/logout",
    [PageMasterMode.FORBIDDEN]: "/forbidden",
    [PageMasterMode.EMPTY]: "/empty",
    [PageMasterMode.LIST]: "/list",
    [PageMasterMode.JOIN]: "/j/:id",
    [PageMasterMode.LOBBY]: "/lobby/:id",
    [PageMasterMode.PLAYER]: "/player/:id",
    [PageMasterMode.COMPOSITION]: `${COMPOSITION_ROOT_PATH}/:id`,
    [PageMasterMode.MEETING]: "/meeting/:id",
    [PageMasterMode.MEETING_DASHBOARD]: "/meeting/:id/dashboard",
    [PageMasterMode.MEETING_HIGHLIGHT]: "/meeting/:id/highlight",
    [PageMasterMode.MEETING_CALL]: "/meeting/:id/call",
    [PageMasterMode.MEETING_GROUP]: "/meeting/:id/group",
    [PageMasterMode.MEETING_PEDAGOGY]: "/meeting/:id/pedagogy",
    [PageMasterMode.MEETING_COMMUNICATION]: "/meeting/:id/communication",
    [PageMasterMode.DASHBOARD]: "/dashboard",
    [PageMasterMode.OVERVIEW]: "/overview",
    [PageMasterMode.WAITING_ROOM]: "/waiting-room",
  },
  [SessionMode.SLAVE]: {
    [PageSlaveMode.MEETING]: "/meeting",
  },
};

export interface PageContextEventListenerMap<M extends SessionMode> {
  onBeforeGoto: <T extends PageMap[M]>(path: string, page: T, params?: any, options?: PageOptions) => void;
}

export interface PageOptions extends NavigateOptions {
  mode?: SessionMode;
  queries?: { [key: string]: string | undefined };
  /**
   * Catalogued page queries (NavigateGlobalQuery) of the CURRENT url to carry
   * onto the target, on top of the always-carried sticky queries. Explicit
   * `queries` win over forwarded values.
   */
  forwardParams?: NavigateGlobalQuery[];
  new?: boolean; // new tab
}

export interface PageContextState<M extends SessionMode> {
  readonly from: {
    readonly location?: Location;
    readonly params?: Params;
    readonly page?: PageMap[M];
  };
  readonly location: Location;
  readonly page?: PageMap[M];
  readonly searchParams: URLSearchParams;
  readonly mode: M;
  readonly params?: Params;
  readonly isLoading: boolean;
  update: <T extends SessionMode>(mode: T, page: PageMap[T], params?: any) => void;
  goTo: <T extends PageMap[M]>(page: T, params?: any, options?: PageOptions) => boolean;
  canGoPreviousPage: () => boolean;
  goToPrevious: <T extends PageMap[M]>(defaultPage?: T, params?: any, options?: PageOptions) => boolean;
  openSettingsPopup: () => void;
  getPreviousPage: () => PageMap[M] | undefined;
  setLoading: (value: boolean, message?: string) => void;
  addEventListener: <K extends keyof PageContextEventListenerMap<M>>(
    key: K,
    callback: PageContextEventListenerMap<M>[K]
  ) => AddEventListernerResult<PageContextEventListenerMap<M>, K>;
  removeEventListener: <K extends keyof PageContextEventListenerMap<M>>(key: K, callback: PageContextEventListenerMap<M>[K]) => void;
}
export const PageContext = React.createContext<PageContextState<any> | undefined>(undefined);

export const getPageUrl = <M extends SessionMode, T extends PageMap[M]>(mode: M, page: T, prevQueryParams: URLSearchParams, params?: any, options?: PageOptions): string => {
  let path = (PAGE_PATHS[mode] as any)[page];
  const queries = options?.queries ?? {};
  if (params) {
    const keys = Object.keys(params);
    keys.forEach((k) => {
      const regex = new RegExp(`(/:${k})(/|$)`);
      const param = (params as any)[k];
      path = path.replace(regex, `/${param}$2`);
    });
  }
  if (queries) {
    // Only sticky queries (slave, settings, screen…) are carried forward
    // automatically; page-specific queries must be passed explicitly, either
    // as `queries` values or as `forwardParams` names read from the current URL.
    const stickyQueryParams = Object.fromEntries(getStickySearchParams(prevQueryParams, options?.forwardParams).entries());
    const mergedQueryParams = { ...stickyQueryParams, ...queries };
    const entries = Object.entries(mergedQueryParams).filter(([, value]) => value !== undefined && value !== "");
    if (entries.length) {
      // Encode values so a query carrying its own sub-URL (e.g. `redirect`,
      // whose value embeds `?`/`&`/`/`) survives round-tripping instead of
      // leaking its parts into the top-level query string.
      const urlQueries = entries.map(([k, v]) => `${encodeURIComponent(k)}=${encodeURIComponent(v as string)}`).join("&");
      path = `${path}?${urlQueries}`;
    }
  }
  return path;
};

export const PageProvider = <M extends SessionMode>({ children }: { children: React.ReactNode }) => {
  const eventListenersRef = useEventListenersRef<keyof PageContextEventListenerMap<M>, PageContextEventListenerMap<M>>();
  const location = useLocation();
  const previousLocation = useRef<Location>();
  const previousParams = useRef<Readonly<Params<string>>>();
  const navigate = useNavigate();
  const store = useAppStore();
  const mode = useAppStoreSelector(state => state.mode);
  const [page, setPage] = useState<PageMap[M] | undefined>();
  const previousPageRef = useRef<PageMap[M]>();
  const paramsRef = useRef<Params>();
  const settingsPopupRef = useRef<SettingsPopupRef>(null);
  const [loading, setLoading] = useState(false);
  const [loadingMessage, setLoadingMessage] = useState<string | undefined>();
  const logger = useLogger();

  // Memoized so consumers only re-render when the page actually changes
  // (location / page / mode / loading). Methods read the mutable refs at call
  // time, so a stable identity never serves stale navigation history.
  const state = useMemo<PageContextState<M>>(() => {
    const searchParams = new URLSearchParams(location?.search);

    const goTo = <T extends PageMap[M]>(nextPage: T, params?: any, options?: PageOptions) => {
      if (!mode) return false;
      const path = getPageUrl(mode, nextPage, searchParams, params, options);
      if (options?.new) {
        const newWindow = window.open(path, '_blank', 'noopener,noreferrer')
        if (newWindow) newWindow.opener = null
        return newWindow ? true : false;
      }
      eventListenersRef.current.triggerEvent("onBeforeGoto", path, nextPage, params, options);
      logger.log("[PAGE] Go to : ", nextPage, " params : ", params, " path : ", path);
      if (!options?.replace) {
        previousLocation.current = location;
        previousParams.current = paramsRef.current;
        previousPageRef.current = page;
      }
      navigate(path, options);
      if (options?.mode && options.mode !== mode) {
        store.dispatch("setMode", options.mode);
      }
      setPage(nextPage);
      return true;
    };

    return {
      from: {
        location: previousLocation.current,
        params: previousParams.current,
        page: previousPageRef.current,
      },
      location,
      page: page,
      searchParams,
      mode: mode as any,
      get params() { return paramsRef.current },
      isLoading: loading,
      update: <T extends SessionMode>(mode: T, page: PageMap[T], params?: Params) => {
        store.dispatch("setMode", mode);
        setPage(page as any);
        paramsRef.current = params;
      },
      goTo,
      canGoPreviousPage: () => {
        const nextRoute = previousLocation.current;
        const nextPath = nextRoute?.pathname;
        if (!nextPath || nextRoute === location) return false;
        return true;
      },
      goToPrevious: <T extends PageMap[M]>(defaultPage?: T, params?: any, options?: PageOptions) => {
        if (!mode) return false;
        const nextRoute = previousLocation.current;
        const nextPath = nextRoute?.pathname;
        const previousPage = previousPageRef.current;
        logger.log("[PAGE] Go to previous : ", previousPage, nextPath);
        previousLocation.current = location;
        previousParams.current = paramsRef.current;
        previousPageRef.current = page;
        if (!nextPath || nextPath === location?.pathname) {
          if (defaultPage) {
            goTo(defaultPage, params, options);
          } else {
            const fallbackPage = (PageMap[mode] as any).LOGIN;
            setPage(fallbackPage);
            navigate(applyStickyParams((PAGE_PATHS[mode] as any)[fallbackPage], location.search));
          }

        } else {
          setPage(previousPage);
          navigate(applyStickyParams(nextPath, location.search));
        }
        return true;
      },
      openSettingsPopup: () => {
        return settingsPopupRef.current?.openPopup();
      },
      setLoading: (value, message) => {
        setLoading(value);
        if (value) setLoadingMessage(message);
        else setLoadingMessage(undefined);
      },
      getPreviousPage: () => previousPageRef.current,
      addEventListener: <K extends keyof PageContextEventListenerMap<M>>(key: K, callback: PageContextEventListenerMap<M>[K]) =>
        eventListenersRef.current?.addEventListener(key, callback),
      removeEventListener: <K extends keyof PageContextEventListenerMap<M>>(key: K, callback: PageContextEventListenerMap<M>[K]) =>
        eventListenersRef.current?.removeEventListener(key, callback),
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location, page, mode, loading, navigate, store, logger]);

  return (
    <PageContext.Provider value={state}>
      {children}
      <SettingsPopup ref={settingsPopupRef} />
      {loading ? <AppLoadingView message={loadingMessage} /> : null}
    </PageContext.Provider>
  );
};

export const usePageContext = <M extends SessionMode = any>(): PageContextState<M> | undefined => {
  return useContext(PageContext);
};

export const usePageContextRef = <M extends SessionMode = any>(): { current: PageContextState<M> | undefined } => {
  const context = usePageContext();
  const ref = useRef<PageContextState<M> | undefined>(context);
  ref.current = context;
  return ref;
};

export const pageContextIsSessionMode = <M extends SessionMode>(mode: M, context: PageContextState<any>): context is PageContextState<M> => {
  return context?.mode === mode;
};
